import React from 'react';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Theme
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'react-feather';
import { Label } from 'src/components/Tags';
import { getMessage } from 'src/components/Tasks/List/utils/getMessage';
import getInitials from 'src/utils/getInitials';
import { CapitalizeNames } from 'src/utils/capitalize';
import { getTaskActionsColor } from 'src/utils/colors';
import { IAppointment } from 'src/interfaces/appointment';
import useAuth from 'src/hooks/useAuth';
import { isPermissionGranted } from 'src/utils/permissions/isPermissionGranted';
import {
    CREATE_PERMISSION,
    LEAD_APPOINTMENTS_CALENDAR_AUTH,
} from 'src/constants/permissions';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        height: '100%',
    },
    avatar: {
        width: 48,
        height: 48,
    },
    containerComment: {
        display: 'flex',
        gap: theme.spacing(2),
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    followUpButton: {
        fontSize: 14,
        height: 35,
        whiteSpace: 'nowrap',
    },
}));

const getAppointmentActionsColor = (action: string) => {
    if (action === 'drive') return 'deepPurple';
    if (action === 'delivery') return 'pink';
    return getTaskActionsColor(action);
};

type AppointmentCardProps = {
    appointment: IAppointment;
    setOpen: (open: boolean) => void;
    isFormOpen: boolean;
};

export const AppointmentCard = ({
    appointment,
    setOpen,
    isFormOpen,
}: AppointmentCardProps) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { user } = useAuth();

    const { user: appointmentUser, createdAt, startDate, description, action } =
        appointment;

    const canCreateAppointment = isPermissionGranted({
        permissions: user?.permissions || {},
        permission: LEAD_APPOINTMENTS_CALENDAR_AUTH,
        crud: CREATE_PERMISSION,
    });

    const handleOpen = () => setOpen(true);

    const actions = action ? [action] : [];

    return (
        <Card className={classes.root} elevation={0}>
            <CardContent className={classes.content}>
                <Box className={classes.containerComment}>
                    <Avatar
                        className={classes.avatar}
                        src={appointmentUser?.image}
                    >
                        {getInitials(appointmentUser?.name)}
                    </Avatar>
                    <Box
                        display="flex"
                        flexDirection="column"
                        gridGap={5}
                        flexGrow={1}
                    >
                        <Typography variant="h4" color="textPrimary">
                            {CapitalizeNames(appointmentUser?.name)}
                        </Typography>
                        <Typography color="textSecondary" variant="caption">
                            {t('Comments.Next')}:{' '}
                            <Label color="cyan">
                                <p style={{ fontSize: 12 }}>
                                    {moment(startDate || createdAt).format(
                                        'DD MMMM YYYY - hh:mm a'
                                    )}
                                </p>
                            </Label>
                        </Typography>
                    </Box>

                    {!isFormOpen && canCreateAppointment && (
                        <Button
                            endIcon={<ArrowRight />}
                            onClick={handleOpen}
                            variant="text"
                            className={classes.followUpButton}
                        >
                            {t('Buttons.FollowUp')}
                        </Button>
                    )}
                </Box>

                <Box display="flex" gridGap={5}>
                    {actions.map((element) => (
                        <Label key={element} color={getAppointmentActionsColor(element)}>
                            <p>{t(`KeysComment.${element}`)}</p>
                        </Label>
                    ))}
                </Box>

                <Typography variant="body1" color="textPrimary">
                    {getMessage(description || '', t)}
                </Typography>
            </CardContent>
        </Card>
    );
};

